import * as i0 from '@angular/core';
import { Component, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

const BUTTON_HOST_ATTRIBUTES = [
    'rs-button',
    'rs-raised-button',
    'rs-outlined-button'
];
/**
 * Button Component
 *
 * @description A button component that comes in a few styles and few colors
 * @example
 * <!-- rs-doc {link: 'Example of link type buttons all colors' } -->
 * <button rs-button color="basic">Default</button>
 * <button rs-button>Primary</button>
 * <button rs-button color="secondary">Secondary</button>
 * @example
 * <!-- rs-doc {link: 'Example of link type buttons disabled' } -->
 * <button rs-button disabled color="basic">Default</button>
 * <button rs-button disabled>Primary</button>
 * <button rs-button disabled color="secondary">Secondary</button>
 * @example
 * <!-- rs-doc {link: 'Example of button group' } -->
 * <button rs-button>Button 1</button>
 * <button rs-button style="border-left: 2px solid lightgray; border-right: 2px solid lightgray;">Button 2</button>
 * <button rs-button>Button 3</button>
 * @example
 * <!-- rs-doc {link: 'Example of regular buttons all colors' } -->
 * <button rs-raised-button color="basic">Default</button>
 * <button rs-raised-button>Primary</button>
 * <button rs-raised-button color="secondary">Secondary</button>
 * @example
 * <!-- rs-doc {link: 'Example of regular buttons disabled' } -->
 * <button rs-raised-button disabled color="basic">Default</button>
 * <button rs-raised-button disabled>Primary</button>
 * <button rs-raised-button disabled color="secondary">Secondary</button>
 * @example
 * <!-- rs-doc {link: 'Example of outlined type buttons all colors' } -->
 * <button rs-outlined-button color="basic">Default</button>
 * <button rs-outlined-button>Primary</button>
 * <button rs-outlined-button color="secondary">Secondary</button>
 * @example
 * <!-- rs-doc {link: 'Example of outlined type buttons disabled' } -->
 * <button rs-outlined-button disabled color="basic">Default</button>
 * <button rs-outlined-button disabled>Primary</button>
 * <button rs-outlined-button disabled color="secondary">Secondary</button>
 */
class ButtonComponent {
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        this._color = 'primary';
        for (const attr of BUTTON_HOST_ATTRIBUTES) {
            if (this._hasHostAttributes(attr)) {
                this._getHostElement().classList.add(attr);
            }
        }
    }
    ngOnInit() {
        this.color = this.color;
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    /* Gets whether the button has one of the given attributes. */
    _hasHostAttributes(...attributes) {
        return attributes.some(attribute => this._getHostElement().hasAttribute(attribute));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ButtonComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: ButtonComponent, isStandalone: true, selector: "button[rs-button], button[rs-raised-button], button[rs-outlined-button]", inputs: { color: "color" }, ngImport: i0, template: "<ng-content></ng-content>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host.rs-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;background-color:transparent;min-height:20px}:host.rs-button:disabled{cursor:not-allowed}:host.rs-button ::ng-deep>svg{vertical-align:middle}:host.rs-raised-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;box-shadow:0 6px 16px -1px #0000003d;border-radius:3px}:host.rs-raised-button:disabled{cursor:not-allowed}:host.rs-outlined-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;border-radius:3px;border-width:2px;background-color:transparent}:host.rs-outlined-button:disabled{cursor:not-allowed}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: `button[rs-button], button[rs-raised-button], button[rs-outlined-button]`, standalone: true, template: "<ng-content></ng-content>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host.rs-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;background-color:transparent;min-height:20px}:host.rs-button:disabled{cursor:not-allowed}:host.rs-button ::ng-deep>svg{vertical-align:middle}:host.rs-raised-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;box-shadow:0 6px 16px -1px #0000003d;border-radius:3px}:host.rs-raised-button:disabled{cursor:not-allowed}:host.rs-outlined-button{cursor:pointer;display:inline-block;line-height:1.42857143;text-align:center;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;background-image:none;border:1px solid transparent;position:relative;transition:background .25s ease-in-out;white-space:normal;word-wrap:break-word;font-weight:700;font-size:1em;min-width:120px;min-height:40px;padding:9px 20px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-family:inherit;border-radius:3px;border-width:2px;background-color:transparent}:host.rs-outlined-button:disabled{cursor:not-allowed}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { color: [{
                type: Input
            }] } });

class ButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: ButtonModule, imports: [CommonModule,
            ButtonComponent], exports: [ButtonComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ButtonModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ButtonComponent
                    ],
                    exports: [ButtonComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonComponent, ButtonModule };
